import { CloseOutlined } from '@ant-design/icons';
import { TrashIcon } from '@heroicons/react/24/outline';
import { default as Collapse } from 'antd/es/collapse';
import type { CollapseProps } from 'antd/es/collapse';
import { default as Divider } from 'antd/es/divider';
import { default as Modal } from 'antd/es/modal';
import Skeleton from 'antd/es/skeleton';
import { useCallback, useMemo, useState } from 'react';
import type { Dispatch } from 'react';
import { useSelector } from 'react-redux';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { GetCustomerCurrentUser200 } from 'api';
import {
  useDeleteDisbursementPayoutDraftsId,
  useGetCustomerConfigurations,
  useGetCustomerUsersUserId,
  useGetDisbursementPayoutDraftsDraftId,
  usePostDisbursementPayoutDraftsDraftIdApprove,
  usePostDisbursementPayoutDraftsDraftIdReject
} from 'api';
import { Restricted } from 'containers/Restricted';

import { selectUserId } from 'reduxLocal/slices/authSlice';

import type { Reviewer } from 'models/ApprovalWorkFlow';

import { useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useGoogleAnalyticsDispatcher } from 'hooks/useGoogleAnalyticsDispatcher';
import { usePermissions } from 'hooks/usePermissions';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';
import { TertiaryButton } from '@atoms/buttons/TertiaryButton';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { getCollapseItems } from './collapseItems';
import { TRANSLATIONS } from './constants';
import { formatApprovalNames } from './util';

import {
  PAYOUT_DRAFT_DETAILS_CONFIRM_DELETE_BUTTON_TEST_ID,
  PAYOUT_DRAFTS_DETAILS_DELETE_BUTTON_TEST_ID,
  PAYOUT_DRAFTS_DETAILS_TEST_ID
} from '../constants';

export type PAYOUT_DRAFTS_KNOWN_SECTIONS =
  | 'payout'
  | 'sender'
  | 'receiver_bank'
  | 'receiver'
  | 'attachments'
  | 'intermediary_agent'
  | 'intermediary_agent_account';

type CollapseItemType = NonNullable<CollapseProps['items']>[number];

export interface CollapseItemWithTypedSection
  extends Omit<CollapseItemType, 'key'> {
  key?: PAYOUT_DRAFTS_KNOWN_SECTIONS;
}

type Props = {
  payoutDraftId: number;
  currentUser: GetCustomerCurrentUser200 | undefined;
  onClose: () => void;
  onRemoveElementFromTable: Dispatch<void>;
};

export const PayoutDraftDetails = ({
  payoutDraftId,
  currentUser,
  onClose,
  onRemoveElementFromTable
}: Props) => {
  const { isPayoutDraftApprovalFlowEnabled } = useFeatureAccessControl();
  const { isPermitted } = usePermissions();
  const openNotification = useOpenNotification();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);
  const userId = useSelector(selectUserId);
  if (!userId) throw new Error('Missing user id');
  const organisationId = useOrganisationId<number>();

  const {
    data: { data: payoutDraftData } = {},
    isLoading,
    isError,
    isSuccess
  } = useGetDisbursementPayoutDraftsDraftId(organisationId, payoutDraftId);

  const { data: { data: orgSettings } = {} } =
    useGetCustomerConfigurations(organisationId);

  const { data: { data: user } = {} } = useGetCustomerUsersUserId(
    organisationId,
    userId
  );

  const dispatchGAEvent = useGoogleAnalyticsDispatcher();

  const isApprovedByUser = useMemo(() => {
    return payoutDraftData?.approvals?.some(
      (approval: Reviewer) => approval.user_id === userId
    );
  }, [payoutDraftData?.approvals, userId]);

  if (!isPayoutDraftApprovalFlowEnabled) {
    onClose();
  }

  const requiredNumberOfApprovals = Number(
    orgSettings!.manual_payout_number_of_approvers
  );

  const remainingApprovals =
    requiredNumberOfApprovals - (payoutDraftData?.approvals?.length ?? 0);

  const { mutateAsync: deletePayoutDraftMutation } =
    useDeleteDisbursementPayoutDraftsId();

  const deletePayoutDraft = useCallback(async () => {
    try {
      setIsDeleteDialogOpen(false);
      await deletePayoutDraftMutation({ organisationId, id: payoutDraftId });
      openNotification({
        kind: 'success',
        message: TRANSLATIONS.DELETE_PAYOUT_DRAFT_SUCCESS(payoutDraftId)
      });
      onRemoveElementFromTable();
    } catch (_) {
      openNotification({
        kind: 'error',
        message: `${TRANSLATIONS.DELETE_PAYOUT_DRAFT_ERROR}`
      });
    } finally {
      onClose();
    }
  }, [
    deletePayoutDraftMutation,
    onClose,
    onRemoveElementFromTable,
    openNotification,
    organisationId,
    payoutDraftId
  ]);

  const { mutate: approvePayoutDraftMutation } =
    usePostDisbursementPayoutDraftsDraftIdApprove();
  const { mutate: rejectPayoutDraftMutation } =
    usePostDisbursementPayoutDraftsDraftIdReject();

  const handleApprove = useCallback(() => {
    approvePayoutDraftMutation(
      {
        organisationId,
        draftId: payoutDraftId
      },
      {
        onSuccess: () => {
          openNotification({
            kind: 'success',
            message: TRANSLATIONS.APPROVE_PAYOUT_DRAFT_SUCCESS(payoutDraftId)
          });
          onRemoveElementFromTable();
          dispatchGAEvent('manual_payout_approval', {
            approval: 'approve'
          });
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: TRANSLATIONS.APPROVE_PAYOUT_DRAFT_ERROR
          });
        },
        onSettled: () => {
          onClose();
        }
      }
    );
  }, [
    approvePayoutDraftMutation,
    organisationId,
    payoutDraftId,
    dispatchGAEvent,
    openNotification,
    onRemoveElementFromTable,
    onClose
  ]);

  const handleReject = useCallback(() => {
    rejectPayoutDraftMutation(
      {
        organisationId,
        draftId: payoutDraftId
      },
      {
        onSuccess: () => {
          openNotification({
            kind: 'success',
            message: TRANSLATIONS.REJECT_PAYOUT_DRAFT_SUCCESS(payoutDraftId)
          });
          onRemoveElementFromTable();
          dispatchGAEvent('manual_payout_approval', {
            approval: 'reject'
          });
        },
        onError: () => {
          openNotification({
            kind: 'error',
            message: TRANSLATIONS.REJECT_PAYOUT_DRAFT_ERROR
          });
        },
        onSettled: () => {
          onClose();
        }
      }
    );
  }, [
    rejectPayoutDraftMutation,
    organisationId,
    payoutDraftId,
    openNotification,
    onRemoveElementFromTable,
    dispatchGAEvent,
    onClose
  ]);

  const hasApprovals = Boolean(payoutDraftData?.approvals?.length);

  const canApproveBase =
    isPayoutDraftApprovalFlowEnabled &&
    !isApprovedByUser &&
    payoutDraftData !== undefined &&
    payoutDraftData.status === 'waiting_review' &&
    !payoutDraftData.rejections?.length &&
    payoutDraftData.user_id !== userId;

  const canApprove = canApproveBase && isPermitted('payout_drafts:review');

  const collapseItems: CollapseItemWithTypedSection[] = useMemo(() => {
    return getCollapseItems(payoutDraftData, isSuccess);
  }, [isSuccess, payoutDraftData]);

  return (
    <div
      style={{ flex: 0.25 }}
      data-testid={PAYOUT_DRAFTS_DETAILS_TEST_ID}
      className="cool-shadow flex flex-col overflow-hidden rounded-xl bg-white"
    >
      <div className="flex justify-between rounded-t-xl px-6 py-3.5 text-xl">
        <span>Details</span>
        <TertiaryButton icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <div className="flex-1 overflow-auto">
        <div className="flex flex-col gap-3 p-6">
          {isLoading && <Skeleton />}
          {isSuccess ? (
            <div>
              {payoutDraftData?.rejections &&
              payoutDraftData.rejections.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <span className="mr-1 font-medium">Declined by: </span>
                  {payoutDraftData.rejections.map((rejection) => (
                    <div
                      key={rejection.user_id}
                      className="flex items-center justify-between gap-3 py-3 first-of-type:p-0"
                    >
                      <div
                        className="font-medium"
                        data-testid="payout-draft-details-declined-by"
                      >
                        {rejection.user_id === userId
                          ? 'You'
                          : rejection.user_name}
                      </div>
                      <div className="text-right">
                        <FormattedDateTime value={rejection.created_at} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                payoutDraftData?.approvals && (
                  <div className="flex flex-col pb-3">
                    {hasApprovals && (
                      <div
                        className="flex flex-col"
                        data-testid="payout-draft-details-approved-by"
                      >
                        <span className="mr-1 font-medium">Approved by: </span>
                        {formatApprovalNames(
                          payoutDraftData?.approvals,
                          userId
                        )}
                      </div>
                    )}
                    {remainingApprovals !== 0 && (
                      <div
                        className="rounded-2xl py-2 text-sm"
                        data-testid="payout-draft-details-needs-approval-by"
                      >
                        Needs to be approved by{' '}
                        {remainingApprovals === 1
                          ? '1 person.'
                          : `${remainingApprovals} people.`}
                      </div>
                    )}
                    {canApprove && (
                      <div className="flex space-x-2">
                        <SecondaryButton
                          onClick={() => setIsDeclineDialogOpen(true)}
                          data-testid="payout-draft-details-button-decline"
                          danger
                          className="flex-1"
                        >
                          Decline
                        </SecondaryButton>

                        <PrimaryButton
                          onClick={() => handleApprove()}
                          data-testid="payout-draft-details-button-approve"
                          className="flex-1"
                        >
                          Approve
                        </PrimaryButton>
                      </div>
                    )}
                  </div>
                )
              )}
              <Modal
                open={isDeclineDialogOpen}
                onCancel={() => setIsDeclineDialogOpen(false)}
                footer={
                  <div className="grid w-full grid-cols-2 gap-4">
                    <SecondaryButton
                      onClick={() => setIsDeclineDialogOpen(false)}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton danger onClick={handleReject}>
                      Decline
                    </PrimaryButton>
                  </div>
                }
              >
                <div className="text-lg font-bold">Decline payout?</div>
                <div className="mb-4">
                  This payout will be declined and will not be processed.
                </div>
              </Modal>
            </div>
          ) : (
            <Skeleton />
          )}
          {isError && (
            <div className="flex h-full items-center justify-center">
              Something went wrong
            </div>
          )}
        </div>
        {isSuccess && (
          <Collapse
            className="antd-details-collapse bg-white pb-0"
            expandIconPosition="end"
            accordion
            bordered={false}
            items={collapseItems}
          />
        )}
        <Modal
          open={isDeleteDialogOpen}
          onCancel={() => setIsDeleteDialogOpen(false)}
          centered
          styles={{
            mask: {
              backgroundColor: 'var(--tenant-surface-overlay)'
            },
            content: {
              width: 440,
              paddingTop: 12,
              backgroundColor: 'rgb(255, 255, 255)'
            }
          }}
          destroyOnHidden
          okText="Yes"
          okButtonProps={{ ['data-testid']: 'confirm-button' }}
          title={TRANSLATIONS.DELETE_PAYOUT_DRAFT_TITLE}
          footer={
            <div className="flex w-full justify-end gap-6">
              <SecondaryButton onClick={() => setIsDeleteDialogOpen(false)}>
                {TRANSLATIONS.NO}
              </SecondaryButton>
              <PrimaryButton
                danger
                onClick={deletePayoutDraft}
                data-testid={PAYOUT_DRAFT_DETAILS_CONFIRM_DELETE_BUTTON_TEST_ID}
              >
                {TRANSLATIONS.YES}
              </PrimaryButton>
            </div>
          }
        >
          {TRANSLATIONS.DELETE_ARE_YOU_SURE}
        </Modal>
        {isSuccess && payoutDraftData?.user_id === currentUser?.user?.id && (
          <Restricted requiredPermission="payout_drafts:manage">
            <div className="px-6 pb-6">
              <Divider />
              <SecondaryButton
                className="flex w-full items-center justify-center"
                danger
                onClick={() => setIsDeleteDialogOpen(true)}
                data-testid={PAYOUT_DRAFTS_DETAILS_DELETE_BUTTON_TEST_ID}
              >
                <TrashIcon className="mr-2 size-6" />
                Delete draft
              </SecondaryButton>
            </div>
          </Restricted>
        )}
      </div>
    </div>
  );
};
